/**
 * 02 · Why trust us.
 *
 * The one screen in the funnel that is about us rather than about the person,
 * so it is kept short and every claim on it is checkable: the science, the
 * model's own size, and the refund we actually honour.
 */
import React from 'react';
import { c, hair, f, ACT } from '../app/tokens';
import { Body, Col, Meta, Muted, Row, Appear } from '../app/primitives';
import { useTt } from '../i18n/useT';
import { accent, Card, Cta, Screen, Serif, StickyActions, TopBar, useWide } from './ui';

const POINTS = ['pw.trust.p1', 'pw.trust.p2', 'pw.trust.p3'];

const Point: React.FC<{ n: number; title: string; body: string; wide?: boolean }> = ({ n, title, body, wide }) => (
    <Row
        style={{
            gap: wide ? 16 : 13,
            alignItems: 'flex-start',
            padding: wide ? '20px 22px' : '15px 16px',
            background: c.card,
            border: hair,
            borderRadius: 16,
        }}
    >
        <div
            style={{
                width: wide ? 38 : 34,
                height: wide ? 38 : 34,
                borderRadius: wide ? 12 : 11,
                flex: '0 0 auto',
                display: 'grid',
                placeItems: 'center',
                background: 'rgba(232,230,224,0.04)',
                border: hair,
            }}
        >
            <span style={{ fontFamily: f.mono, fontSize: wide ? 16 : 14, fontWeight: 500, color: c.text }}>{n}</span>
        </div>
        <Col style={{ flex: 1, minWidth: 0 }} gap={wide ? 8 : 6}>
            <Meta style={{ fontSize: wide ? 11.5 : 10.5, color: c.textPri, letterSpacing: '0.13em', lineHeight: 1.35 }}>
                {title}
            </Meta>
            <Muted style={{ fontSize: wide ? 13.5 : 12.5, whiteSpace: 'pre-line' }}>{body}</Muted>
        </Col>
    </Row>
);

const Hook: React.FC<{ wide?: boolean }> = ({ wide }) => {
    const { t } = useTt();

    return (
        <div style={{ paddingLeft: wide ? 19 : 17, borderLeft: `2px solid ${ACT}` }}>
            <Serif italic size={wide ? 21 : 17.5} style={{ color: c.muted, lineHeight: 1.4, letterSpacing: '-0.01em' }}>
                {t('pw.trust.hook')}
            </Serif>
            <Serif size={wide ? 30 : 27} style={{ marginTop: wide ? 15 : 14, lineHeight: 1.25 }}>
                {accent(t('pw.trust.hook2'))}
            </Serif>
        </div>
    );
};

const Mission: React.FC<{ wide?: boolean; style?: React.CSSProperties }> = ({ wide, style }) => {
    const { t } = useTt();

    return (
        <Card
            accent={`${ACT}30`}
            style={{
                background: `linear-gradient(160deg, ${ACT}12, ${c.card} 62%)`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                ...style,
            }}
        >
            <Meta style={{ fontSize: wide ? 10 : 9.5, color: ACT, letterSpacing: '0.16em' }}>
                {t('pw.trust.mission.label')}
            </Meta>
            <Body style={{ fontSize: wide ? 15 : 14, marginTop: wide ? 12 : 11 }}>{t('pw.trust.mission.body')}</Body>
        </Card>
    );
};

const Trust: React.FC<{ onNext: () => void; onBack?: () => void }> = ({ onNext, onBack }) => {
    const { t } = useTt();
    const wide = useWide();
    const points = POINTS.map((k, i) => ({ n: i + 1, title: t(`${k}.title`), body: t(`${k}.body`) }));

    if (wide) {
        return (
            <Screen wide style={{ padding: '38px 40px 56px' }}>
                <Appear>
                    <Serif size={40} style={{ textAlign: 'center', lineHeight: 1.14, letterSpacing: '-0.026em' }}>
                        {t('pw.trust.title')}
                    </Serif>
                </Appear>
                {/* Both columns stretch and the mission card takes the slack, so
                    the two stacks end on the same line instead of one dangling. */}
                <Row style={{ gap: 30, marginTop: 38, alignItems: 'stretch' }}>
                    <Appear delay={100} style={{ width: 520, flex: '0 0 auto', display: 'flex' }}>
                        <Col style={{ flex: 1 }} gap={18}>
                            <Hook wide />
                            <Mission wide style={{ flex: 1 }} />
                        </Col>
                    </Appear>
                    <Appear delay={180} style={{ flex: 1, display: 'flex', minWidth: 0 }}>
                        <Col style={{ flex: 1 }} gap={14}>
                            {points.map((p) => (
                                <Point key={p.title} n={p.n} title={p.title} body={p.body} wide />
                            ))}
                        </Col>
                    </Appear>
                </Row>
                <div style={{ marginTop: 38 }}>
                    <Cta label={t('pw.continue')} onClick={onNext} width={320} height={54} />
                </div>
            </Screen>
        );
    }

    return (
        <Screen column>
            <TopBar onBack={onBack} />
            {/* The spare height belongs to ONE wrapper around all the content,
                not to the `Appear` alone — putting it there let the animation
                wrapper grow and shove the three points away from the mission
                card, tearing a hole through the middle of the screen. */}
            <Col style={{ flex: 1, minHeight: 0 }}>
                <Appear>
                    <div style={{ padding: '26px 20px 0' }}>
                        <Serif size={26} style={{ textAlign: 'center' }}>
                            {t('pw.trust.title')}
                        </Serif>
                        <div style={{ marginTop: 26 }}>
                            <Hook />
                        </div>
                    </div>
                    <div style={{ margin: '24px 20px 0' }}>
                        <Mission />
                    </div>
                </Appear>
                <Col style={{ padding: '12px 20px 0' }} gap={9}>
                    {points.map((p) => (
                        <Point key={p.title} n={p.n} title={p.title} body={p.body} />
                    ))}
                </Col>
            </Col>
            <StickyActions>
                <Cta label={t('pw.continue')} onClick={onNext} />
            </StickyActions>
        </Screen>
    );
};

export default Trust;
